// ✅ All existing imports
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  createProductSeo,
  updateProductSeo,
  clearSeoState,
  fetchSeoByProductId,
} from '../../../Reducer/seoSlice';
import {
  Container,
  Typography,
  TextField,
  Button,
  Card,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Fade,
  Zoom,
  Tooltip,
  Paper,
} from '@mui/material';
import { Save, ArrowBack, InfoOutlined, WarningAmber } from '@mui/icons-material';
import { styled } from '@mui/system';
import Seo from '../../seo/Seo';

// ✅ Styling
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& label.Mui-focused': {
    color: '#5c6bc0',
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: '#5c6bc0',
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: '#7986cb',
    },
  },
}));

// ✅ Robots Options
const robotOptions = [
  { label: 'Index', value: 'index', tooltip: 'Allow search engines to index this page' },
  { label: 'No Index', value: 'noindex', tooltip: 'Prevent indexing' },
  { label: 'Follow', value: 'follow', tooltip: 'Allow following links' },
  { label: 'No Follow', value: 'nofollow', tooltip: 'Prevent following links' },
  { label: 'No Archive', value: 'noarchive', tooltip: 'Prevent caching' },
  { label: 'No Image Index', value: 'noimageindex', tooltip: 'Prevent image indexing' },
  { label: 'No Snippet', value: 'nosnippet', tooltip: 'Prevent showing snippets' },
];

const conflictingPairs = [
  ['index', 'noindex'],
  ['follow', 'nofollow'],
];

// ✅ Main Component
const AddSeo = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { seoInfo, isFetched, loading, error } = useSelector((state) => state.seo);
  const hasFetched = useRef(false);

  // ✅ Default state
  const [seoData, setSeoData] = useState({
    seo_title: '',
    seo_keywords: '',
    seo_description: '',
    seo_slug: '',
    canonical_url: '',
    meta_robots: [],
    schema: [],
  });

  useEffect(() => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      dispatch(fetchSeoByProductId(id)).catch((err) => {
        console.error('❌ Failed to fetch SEO info:', err);
      });
    }

    return () => {
      hasFetched.current = false;
      dispatch(clearSeoState());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (seoInfo && seoInfo.seo) {
      const {
        seo_title = '',
        seo_keywords = '',
        seo_description = '',
        seo_slug = '',
        canonical_url = '',
        meta_robots,
        schema = [],
      } = seoInfo.seo;

      setSeoData({
        seo_title,
        seo_keywords,
        seo_description,
        seo_slug,
        canonical_url,
        meta_robots: meta_robots ? meta_robots.split(',').filter(Boolean) : [],
        schema: Array.isArray(schema) ? schema : [],
      });
    }
  }, [seoInfo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSeoData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRobotsChange = (value) => {
    setSeoData((prev) => {
      const metaRobotsArray = Array.isArray(prev.meta_robots) ? [...prev.meta_robots] : [];
      let updated = [...metaRobotsArray];

      if (updated.includes(value)) {
        updated = updated.filter((item) => item !== value);
      } else {
        const conflict = conflictingPairs.find((pair) => pair.includes(value));
        if (conflict) {
          updated = updated.filter((item) => !conflict.includes(item));
        }
        updated.push(value);
      }
      return { ...prev, meta_robots: updated };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const metaRobots = Array.isArray(seoData.meta_robots)
      ? seoData.meta_robots.join(',')
      : '';

    let parsedSchema = [];
    try {
      if (typeof seoData.schema === 'string') {
        parsedSchema = JSON.parse(seoData.schema);
      } else if (Array.isArray(seoData.schema)) {
        parsedSchema = seoData.schema;
      }
    } catch (err) {
      console.error('Invalid schema JSON:', err);
      toast.error('Schema field contains invalid JSON.');
      return;
    }

    const payload = {
      ...seoData,
      meta_robots: metaRobots,
      schema: parsedSchema,
    };

    const action = isFetched
      ? updateProductSeo({ ex_product_id: id, seoData: payload })
      : createProductSeo({ ...payload, ex_product_id: id });

    dispatch(action)
      .unwrap()
      .then(() => {
        toast.success(`SEO info ${isFetched ? 'updated' : 'added'} successfully`);
        navigate(-1);
      })
      .catch((err) => {
        console.error('SEO submission failed:', err);
        toast.error(
          `Failed to ${isFetched ? 'update' : 'add'} SEO info: ${err?.message || 'An error occurred'}`
        );
      });
  };

  return (
    <Fade in timeout={500}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
            {isFetched ? 'Update' : 'Add'} SEO Information
            <Tooltip title="Configure SEO settings for better search visibility">
              <InfoOutlined sx={{ ml: 1 }} />
            </Tooltip>
          </Typography>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Card sx={{ p: 4, borderRadius: 3 }}>
            {error && <Alert severity="error">{error.message || 'Error loading SEO data'}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Input fields */}
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="SEO Title"
                    name="seo_title"
                    value={seoData.seo_title}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="SEO Keywords"
                    name="seo_keywords"
                    value={seoData.seo_keywords}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="SEO Description"
                    name="seo_description"
                    value={seoData.seo_description}
                    onChange={handleChange}
                    required
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="SEO Slug"
                    name="seo_slug"
                    value={seoData.seo_slug}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    label="Canonical URL"
                    name="canonical_url"
                    value={seoData.canonical_url}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Robots Meta */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="h6">Robots Meta Directives</Typography>
                    <FormGroup row>
                      {robotOptions.map(({ label, value, tooltip }) => (
                        <Tooltip key={value} title={tooltip}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={(seoData.meta_robots || []).includes(value)}
                                onChange={() => handleRobotsChange(value)}
                              />
                            }
                            label={label}
                          />
                        </Tooltip>
                      ))}
                    </FormGroup>

                    {/* Conflict Error */}
                    {(() => {
                      const selected = seoData.meta_robots || [];
                      const conflicts = conflictingPairs.find(
                        ([a, b]) => selected.includes(a) && selected.includes(b)
                      );
                      return conflicts ? (
                        <Alert severity="error" icon={<WarningAmber />}>
                          Conflict: {conflicts[0]} and {conflicts[1]} can't be selected together.
                        </Alert>
                      ) : null;
                    })()}
                  </Paper>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="contained" size="large" startIcon={<Save />}>
                  {isFetched ? 'Update SEO' : 'Save SEO'}
                </Button>
              </Box>
            </Box>
          </Card>
        )}

        <Box sx={{ mt: 4 }}>
          <Seo seoData={seoData} setSeoData={setSeoData} />
        </Box>
      </Container>
    </Fade>
  );
};

export default AddSeo;
